// src/pages/supervisor/PendingReview.js

import React, { useState, useEffect, useRef } from 'react';
import {
    Container,
    Typography,
    Button,
    Paper,
    Box,
    Grid,
    Card,
    CardContent,
    Avatar,
    Chip,
    LinearProgress,
    useTheme,
    Tooltip,
    Alert,
    CircularProgress,
    Fade,
    Zoom,
    Divider,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    MenuItem,
    FormControl,
    InputLabel,
    Select,
    Tab,
    Tabs,
    Badge,
    Rating,
    Stack,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    CardActions,
    useMediaQuery
} from '@mui/material';
import {
    Refresh as RefreshIcon,
    Person,
    CheckCircle,
    Warning,
    Schedule,
    School,
    Analytics,
    Verified,
    Pending as PendingIcon,
    Dashboard as DashboardIcon,
    CalendarToday,
    Description,
    Edit,
    Send,
    Visibility,
    ExpandMore,
    PriorityHigh,
    Info,
    Cancel,
    People,
    RateReview
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import useCustomToast from '../../hooks/useToast';
import {
    APIReviewList,
    APIReviewStats,
    APIReviewStart,
    APIReviewSubmit,
    APIReviewEscalate,
    APIReviewUpdateUrgency
} from '../../apiServices/APIs';
import { 
    formatCreatedAtDate, 
    getUserDetails
} from '../../utils/helpers';
import { apiService } from '../../apiServices/ApiService';
import LoadingState from '../../components/LoadingState';
import ErrorState from '../../components/ErrorState';
import SupervisorBar from '../../components/SupervisorBar';
import authService from '../../apiServices/authService';
import { FullWidthContainer } from '../../components/FullWidthContainer';

const PendingReview = () => {
    const theme = useTheme();
    const navigate = useNavigate();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));
    
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [reviews, setReviews] = useState([]);
    const [stats, setStats] = useState(null);
    const [activeTab, setActiveTab] = useState(0);
    const [selectedReview, setSelectedReview] = useState(null);
    const [openReviewDialog, setOpenReviewDialog] = useState(false);
    const [openEscalateDialog, setOpenEscalateDialog] = useState(false);
    const [openUrgencyDialog, setOpenUrgencyDialog] = useState(false);
    const [reviewForm, setReviewForm] = useState({
        decision: '',
        comments: '',
        score: ''
    });
    const [escalateForm, setEscalateForm] = useState({ reason: '' });
    const [urgencyForm, setUrgencyForm] = useState({ level: '' });
    const [actionLoading, setActionLoading] = useState(false);
    const showToast = useCustomToast();
    const isMounted = useRef(true);
    const supervisorId = getUserDetails("Id");

    // Handle logout
    const handleLogout = async () => {
        try {
            await authService.logout();
        } catch (error) {
            console.error('Logout error:', error);
        }
    };

    useEffect(() => {
        isMounted.current = true;
        apiService.setToastFunction(showToast);
        fetchAllData();
        
        const interval = setInterval(() => {
            fetchAllData(true);
        }, 60000);
        
        return () => {
            isMounted.current = false;
            clearInterval(interval);
        };
    }, []);

    const fetchAllData = async (silent = false) => {
        if (!silent) setLoading(true);
        
        try {
            await Promise.all([
                fetchReviews(),
                fetchStats()
            ]);
        } catch (error) {
            if (!silent) {
                setError("Failed to load data");
                console.error("Fetch error:", error);
            }
        } finally {
            if (!silent && isMounted.current) {
                setLoading(false);
            }
        }
    };

    const fetchReviews = async () => {
        const result = await apiService.listById(APIReviewList, supervisorId);
        if (result.success && isMounted.current) {
            setReviews(Array.isArray(result.data) ? result.data : []);
        }
    };

    const fetchStats = async () => {
        const result = await apiService.listById(APIReviewStats, supervisorId);
        if (result.success && result.data && isMounted.current) {
            setStats(result.data);
        }
    };

    const handleStartReview = async (review) => {
        try {
            const formData = new FormData();
            formData.append('review_id', review.id);
            
            const result = await apiService.update(APIReviewStart, formData);
            
            if (result && result.success) {
                showToast("Success", "Review started", "success");
                fetchAllData();
            }
        } catch (error) {
            showToast("Error", "Failed to start review", "error");
        }
    };

    const handleSubmitReview = async () => {
        if (!selectedReview) return;
        
        setActionLoading(true);
        try {
            const formData = new FormData();
            formData.append('review_id', selectedReview.id);
            formData.append('review_decision', reviewForm.decision);
            formData.append('review_comments', reviewForm.comments);
            if (reviewForm.score) formData.append('review_score', reviewForm.score);
            
            const result = await apiService.update(APIReviewSubmit, formData);
            
            if (result && result.success) {
                showToast("Success", "Review submitted", "success");
                fetchAllData();
                handleCloseReviewDialog();
            }
        } catch (error) {
            showToast("Error", "Failed to submit review", "error");
        } finally {
            setActionLoading(false);
        }
    };

    const handleEscalate = async () => {
        if (!selectedReview) return;
        
        setActionLoading(true);
        try {
            const formData = new FormData();
            formData.append('review_id', selectedReview.id);
            formData.append('escalation_reason', escalateForm.reason);
            
            const result = await apiService.update(APIReviewEscalate, formData);
            
            if (result && result.success) {
                showToast("Success", "Review escalated", "success");
                fetchAllData();
                handleCloseEscalateDialog();
            }
        } catch (error) {
            showToast("Error", "Failed to escalate", "error");
        } finally {
            setActionLoading(false);
        }
    };

    const handleUpdateUrgency = async () => {
        if (!selectedReview) return;
        
        setActionLoading(true);
        try {
            const formData = new FormData();
            formData.append('review_id', selectedReview.id);
            formData.append('urgency_level', urgencyForm.level);
            
            const result = await apiService.update(APIReviewUpdateUrgency, formData);
            
            if (result && result.success) {
                showToast("Success", "Urgency level updated", "success");
                fetchAllData();
                handleCloseUrgencyDialog();
            }
        } catch (error) {
            showToast("Error", "Failed to update urgency", "error");
        } finally {
            setActionLoading(false);
        }
    };

    const handleOpenReviewDialog = (review) => {
        setSelectedReview(review);
        setReviewForm({ decision: '', comments: '', score: '' });
        setOpenReviewDialog(true);
    };

    const handleCloseReviewDialog = () => {
        setOpenReviewDialog(false);
        setSelectedReview(null);
        setReviewForm({ decision: '', comments: '', score: '' });
    };

    const handleOpenEscalateDialog = (review) => {
        setSelectedReview(review);
        setEscalateForm({ reason: '' });
        setOpenEscalateDialog(true);
    };

    const handleCloseEscalateDialog = () => {
        setOpenEscalateDialog(false);
        setSelectedReview(null);
        setEscalateForm({ reason: '' });
    };

    const handleOpenUrgencyDialog = (review) => {
        setSelectedReview(review);
        setUrgencyForm({ level: review.urgency_level });
        setOpenUrgencyDialog(true);
    };

    const handleCloseUrgencyDialog = () => {
        setOpenUrgencyDialog(false);
        setSelectedReview(null);
        setUrgencyForm({ level: '' });
    };

    const filteredReviews = () => {
        if (activeTab === 0) return reviews;
        if (activeTab === 1) return reviews.filter(r => r.review_status === 'pending' || r.review_status === 'in_progress');
        if (activeTab === 2) return reviews.filter(r => r.is_overdue);
        if (activeTab === 3) return reviews.filter(r => r.urgency_level === 'urgent' || r.urgency_level === 'critical');
        return reviews;
    };

    if (loading) {
        return <LoadingState message="Loading Reviews..." variant="research" size="large" fullPage />;
    }

    if (error) {
        return <ErrorState error={error} onRetry={fetchAllData} title="Unable to Load Reviews" variant="research" fullPage retryText="Retry Loading" />;
    }

    const displayReviews = filteredReviews();

    return (
        <>
            <FullWidthContainer>
                <Box sx={{ backgroundColor: 'white', borderRadius: 3 }}>
                    <SupervisorBar notificationCount={50} />
                    {/* Header Section */}
                    <Paper elevation={2} sx={{ 
                        p: { xs: 2, sm: 3 }, 
                        mb: { xs: 2, sm: 3 }, 
                        borderRadius: 3,
                        backgroundColor: 'white',
                    }}>
                        <Box sx={{ 
                            display: 'flex', 
                            justifyContent: 'space-between', 
                            alignItems: 'center', 
                            flexWrap: 'wrap', 
                            gap: 2,
                            flexDirection: { xs: 'column', sm: 'row' }
                        }}>
                            <Typography variant="h6" sx={{ 
                                fontWeight: 'bold', 
                                color: theme.palette.primary.main, 
                                display: 'flex', 
                                alignItems: 'center', 
                                gap: 1,
                                fontSize: { xs: '1rem', sm: '1.25rem' }
                            }}>
                                <RateReview /> Pending Reviews ({reviews.length})
                            </Typography>
                            <Button 
                                variant="outlined" 
                                color="info" 
                                startIcon={<RefreshIcon />} 
                                onClick={() => fetchAllData()} 
                                sx={{ 
                                    textTransform: 'none', 
                                    borderRadius: '8px',
                                    width: { xs: '100%', sm: 'auto' }
                                }}
                            >
                                Refresh
                            </Button>
                        </Box>

                        {/* Stats Cards */}
                        {stats && (
                            <Grid container spacing={2} sx={{ mt: 2 }}>
                                <Grid item xs={6} sm={3}>
                                    <Card sx={{ 
                                        textAlign: 'center', 
                                        bgcolor: theme.palette.warning.light + '10',
                                        transition: 'transform 0.2s',
                                        '&:hover': { transform: 'translateY(-4px)' }
                                    }}>
                                        <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
                                            <Typography variant="h4" sx={{ 
                                                fontWeight: 'bold',
                                                fontSize: { xs: '1.5rem', sm: '2rem' }
                                            }}>
                                                {stats.pending}
                                            </Typography>
                                            <Typography variant="caption" sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>
                                                Pending
                                            </Typography>
                                        </CardContent>
                                    </Card>
                                </Grid>
                                <Grid item xs={6} sm={3}>
                                    <Card sx={{ 
                                        textAlign: 'center', 
                                        bgcolor: theme.palette.error.light + '10',
                                        transition: 'transform 0.2s',
                                        '&:hover': { transform: 'translateY(-4px)' }
                                    }}>
                                        <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
                                            <Typography variant="h4" sx={{ 
                                                fontWeight: 'bold',
                                                fontSize: { xs: '1.5rem', sm: '2rem' }
                                            }}>
                                                {stats.overdue}
                                            </Typography>
                                            <Typography variant="caption" sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>
                                                Overdue
                                            </Typography>
                                        </CardContent>
                                    </Card>
                                </Grid>
                                <Grid item xs={6} sm={3}>
                                    <Card sx={{ 
                                        textAlign: 'center', 
                                        bgcolor: theme.palette.warning.light + '10',
                                        transition: 'transform 0.2s',
                                        '&:hover': { transform: 'translateY(-4px)' }
                                    }}>
                                        <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
                                            <Typography variant="h4" sx={{ 
                                                fontWeight: 'bold',
                                                fontSize: { xs: '1.5rem', sm: '2rem' }
                                            }}>
                                                {stats.urgent}
                                            </Typography>
                                            <Typography variant="caption" sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>
                                                Urgent
                                            </Typography>
                                        </CardContent>
                                    </Card>
                                </Grid>
                                <Grid item xs={6} sm={3}>
                                    <Card sx={{ 
                                        textAlign: 'center', 
                                        bgcolor: theme.palette.success.light + '10',
                                        transition: 'transform 0.2s',
                                        '&:hover': { transform: 'translateY(-4px)' }
                                    }}>
                                        <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
                                            <Typography variant="h4" sx={{ 
                                                fontWeight: 'bold',
                                                fontSize: { xs: '1.5rem', sm: '2rem' }
                                            }}>
                                                {stats.average_completion_time}
                                            </Typography>
                                            <Typography variant="caption" sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>
                                                Avg Days
                                            </Typography>
                                        </CardContent>
                                    </Card>
                                </Grid>
                            </Grid>
                        )}
                    </Paper>
                </Box>
                
                {/* Tabs */}
                <Paper elevation={2} sx={{ 
                    borderRadius: 3, 
                    overflow: 'hidden',
                    backgroundColor: 'white',
                    mb: { xs: 2, sm: 3 }
                }}>
                    <Tabs
                        value={activeTab}
                        onChange={(e, v) => setActiveTab(v)}
                        sx={{ 
                            borderBottom: 1, 
                            borderColor: 'divider', 
                            px: { xs: 1, sm: 2 }, 
                            pt: 2,
                            '& .MuiTab-root': {
                                fontSize: { xs: '0.75rem', sm: '0.875rem' },
                                minWidth: { xs: 'auto', sm: 120 },
                                px: { xs: 1, sm: 2 }
                            }
                        }}
                        variant={isMobile ? "scrollable" : "standard"}
                        scrollButtons={isMobile ? "auto" : false}
                    >
                        <Tab label="All" />
                        <Tab 
                            label={
                                <Badge badgeContent={stats?.pending || 0} color="warning">
                                    Pending
                                </Badge>
                            } 
                        />
                        <Tab 
                            label={
                                <Badge badgeContent={stats?.overdue || 0} color="error">
                                    Overdue
                                </Badge>
                            } 
                        />
                        <Tab 
                            label={
                                <Badge badgeContent={stats?.urgent || 0} color="error">
                                    Urgent
                                </Badge>
                            } 
                        />
                    </Tabs>
                </Paper>

                {/* Reviews Grid */}
                <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
                    {displayReviews.length === 0 ? (
                        <Paper sx={{ 
                            p: { xs: 4, sm: 8 }, 
                            textAlign: 'center', 
                            borderRadius: 3,
                            backgroundColor: 'white'
                        }}>
                            <RateReview sx={{ fontSize: { xs: 48, sm: 60 }, color: 'text.secondary', mb: 2 }} />
                            <Typography variant="h6" color="text.secondary" sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                                No reviews pending
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                All caught up!
                            </Typography>
                        </Paper>
                    ) : (
                        <Grid container spacing={2}>
                            {displayReviews.map((review, index) => (
                                <Grid item xs={12} sm={6} lg={4} key={review.id}>
                                    <Zoom in={true} style={{ transitionDelay: `${index * 50}ms` }}>
                                        <Card sx={{ 
                                            height: '100%', 
                                            display: 'flex', 
                                            flexDirection: 'column', 
                                            position: 'relative',
                                            transition: 'transform 0.2s, box-shadow 0.2s',
                                            '&:hover': {
                                                transform: 'translateY(-4px)',
                                                boxShadow: theme.shadows[8]
                                            }
                                        }}>
                                            {/* Urgency Badge */}
                                            {review.urgency_level !== 'normal' && (
                                                <Box sx={{ 
                                                    position: 'absolute', 
                                                    top: 16, 
                                                    right: 16,
                                                    zIndex: 1
                                                }}>
                                                    <Chip 
                                                        icon={review.urgency_level === 'critical' ? <PriorityHigh /> : <Warning />} 
                                                        label={review.formatted_urgency_level} 
                                                        color={review.urgency_level_color} 
                                                        size="small" 
                                                    />
                                                </Box>
                                            )}
                                            
                                            <CardContent sx={{ flexGrow: 1, p: { xs: 2, sm: 3 } }}>
                                                {/* Student Info */}
                                                <Box sx={{ 
                                                    display: 'flex', 
                                                    alignItems: 'center', 
                                                    gap: 2, 
                                                    mb: 2,
                                                    flexDirection: { xs: 'column', sm: 'row' },
                                                    textAlign: { xs: 'center', sm: 'left' }
                                                }}>
                                                    <Avatar 
                                                        src={review.student?.profile_image} 
                                                        sx={{ width: { xs: 48, sm: 50 }, height: { xs: 48, sm: 50 } }}
                                                    >
                                                        <Person />
                                                    </Avatar>
                                                    <Box>
                                                        <Typography variant="subtitle1" sx={{ 
                                                            fontWeight: 'bold',
                                                            fontSize: { xs: '0.9rem', sm: '1rem' }
                                                        }}>
                                                            {review.student?.name}
                                                        </Typography>
                                                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>
                                                            {review.student?.registration_id}
                                                        </Typography>
                                                    </Box>
                                                </Box>
                                                
                                                <Divider sx={{ my: 1.5 }} />
                                                
                                                {/* Work Details */}
                                                <Typography variant="subtitle2" sx={{ 
                                                    fontWeight: 'bold', 
                                                    mb: 1,
                                                    fontSize: { xs: '0.8rem', sm: '0.875rem' }
                                                }}>
                                                    {review.work?.title}
                                                </Typography>
                                                
                                                <Stack spacing={1} sx={{ mb: 2 }}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                                        <Description fontSize="small" color="action" />
                                                        <Typography variant="body2" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                                                            Type: {review.work?.type?.replace('_', ' ')}
                                                        </Typography>
                                                    </Box>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                                        <CalendarToday fontSize="small" color="action" />
                                                        <Typography variant="body2" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                                                            Submitted: {formatCreatedAtDate(review.work?.submitted_at)}
                                                        </Typography>
                                                    </Box>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                                        <Schedule fontSize="small" color="action" />
                                                        <Typography variant="body2" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                                                            Due: {new Date(review.review_due_date).toLocaleDateString()}
                                                        </Typography>
                                                    </Box>
                                                    {review.is_overdue && (
                                                        <Chip 
                                                            icon={<Warning />} 
                                                            label={`Overdue by ${Math.abs(review.days_until_due)} days`} 
                                                            color="error" 
                                                            size="small" 
                                                        />
                                                    )}
                                                </Stack>
                                                
                                                {/* Status Chip */}
                                                <Chip 
                                                    icon={review.review_status_icon} 
                                                    label={review.formatted_review_status} 
                                                    color={review.review_status_color} 
                                                    size="small" 
                                                />
                                            </CardContent>
                                            
                                            <CardActions sx={{ 
                                                p: 2, 
                                                pt: 0, 
                                                gap: 1, 
                                                flexWrap: 'wrap',
                                                justifyContent: { xs: 'center', sm: 'flex-start' }
                                            }}>
                                                {review.review_status === 'pending' && (
                                                    <Button 
                                                        size="small" 
                                                        variant="contained" 
                                                        onClick={() => handleStartReview(review)} 
                                                        startIcon={<Edit />}
                                                        fullWidth={isMobile}
                                                    >
                                                        Start Review
                                                    </Button>
                                                )}
                                                {(review.review_status === 'in_progress' || review.review_status === 'pending') && (
                                                    <Button 
                                                        size="small" 
                                                        variant="contained" 
                                                        color="primary" 
                                                        onClick={() => handleOpenReviewDialog(review)} 
                                                        startIcon={<RateReview />}
                                                        fullWidth={isMobile}
                                                    >
                                                        Submit Review
                                                    </Button>
                                                )}
                                                <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center', width: { xs: '100%', sm: 'auto' } }}>
                                                    <Tooltip title="Update Urgency">
                                                        <IconButton size="small" onClick={() => handleOpenUrgencyDialog(review)}>
                                                            <PriorityHigh />
                                                        </IconButton>
                                                    </Tooltip>
                                                    <Tooltip title="Escalate">
                                                        <IconButton size="small" onClick={() => handleOpenEscalateDialog(review)}>
                                                            <People />
                                                        </IconButton>
                                                    </Tooltip>
                                                    <Tooltip title="View Work">
                                                        <IconButton size="small" onClick={() => window.open(review.work?.file_url, '_blank')}>
                                                            <Visibility />
                                                        </IconButton>
                                                    </Tooltip>
                                                </Box>
                                            </CardActions>
                                        </Card>
                                    </Zoom>
                                </Grid>
                            ))}
                        </Grid>
                    )}
                </motion.div>

                {/* Submit Review Dialog */}
                <Dialog
                    open={openReviewDialog}
                    onClose={handleCloseReviewDialog}
                    maxWidth="md"
                    fullWidth
                    PaperProps={{
                        sx: {
                            m: { xs: 1, sm: 2 },
                            width: { xs: 'calc(100% - 16px)', sm: 'auto' }
                        }
                    }}
                >
                    <DialogTitle sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                        Submit Review
                    </DialogTitle>
                    <DialogContent>
                        <Box sx={{ pt: 2 }}>
                            <Alert severity="info" sx={{ mb: 2 }}>
                                <Typography variant="body2" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                                    <strong>Student:</strong> {selectedReview?.student?.name}<br />
                                    <strong>Work:</strong> {selectedReview?.work?.title}
                                </Typography>
                            </Alert>
                            
                            <FormControl fullWidth sx={{ mb: 2 }} size={isMobile ? "small" : "medium"}>
                                <InputLabel>Decision *</InputLabel>
                                <Select 
                                    value={reviewForm.decision} 
                                    onChange={(e) => setReviewForm({ ...reviewForm, decision: e.target.value })} 
                                    label="Decision *"
                                >
                                    <MenuItem value="approve">Approve</MenuItem>
                                    <MenuItem value="revision_requested">Request Revision</MenuItem>
                                    <MenuItem value="reject">Reject</MenuItem>
                                    <MenuItem value="refer_to_committee">Refer to Committee</MenuItem>
                                </Select>
                            </FormControl>
                            
                            <TextField 
                                fullWidth 
                                label="Review Score (0-100)" 
                                type="number" 
                                inputProps={{ min: 0, max: 100 }} 
                                sx={{ mb: 2 }} 
                                value={reviewForm.score} 
                                onChange={(e) => setReviewForm({ ...reviewForm, score: e.target.value })}
                                size={isMobile ? "small" : "medium"}
                            />
                            
                            <TextField 
                                fullWidth 
                                label="Review Comments *" 
                                multiline 
                                rows={6} 
                                required 
                                value={reviewForm.comments} 
                                onChange={(e) => setReviewForm({ ...reviewForm, comments: e.target.value })} 
                                placeholder="Provide detailed feedback for the student..."
                                size={isMobile ? "small" : "medium"}
                            />
                        </Box>
                    </DialogContent>
                    <DialogActions sx={{ p: { xs: 2, sm: 3 } }}>
                        <Button onClick={handleCloseReviewDialog} size={isMobile ? "small" : "medium"}>Cancel</Button>
                        <Button 
                            onClick={handleSubmitReview} 
                            variant="contained" 
                            disabled={actionLoading || !reviewForm.decision || !reviewForm.comments}
                            size={isMobile ? "small" : "medium"}
                        >
                            {actionLoading ? <CircularProgress size={20} /> : 'Submit Review'}
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* Escalate Dialog */}
                <Dialog
                    open={openEscalateDialog}
                    onClose={handleCloseEscalateDialog}
                    maxWidth="sm"
                    fullWidth
                    PaperProps={{
                        sx: {
                            m: { xs: 1, sm: 2 },
                            width: { xs: 'calc(100% - 16px)', sm: 'auto' }
                        }
                    }}
                >
                    <DialogTitle sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                        Escalate Review
                    </DialogTitle>
                    <DialogContent>
                        <TextField 
                            fullWidth 
                            label="Escalation Reason *" 
                            multiline 
                            rows={4} 
                            sx={{ mt: 2 }} 
                            value={escalateForm.reason} 
                            onChange={(e) => setEscalateForm({ reason: e.target.value })} 
                            placeholder="Explain why this review needs escalation..."
                            size={isMobile ? "small" : "medium"}
                        />
                    </DialogContent>
                    <DialogActions sx={{ p: { xs: 2, sm: 3 } }}>
                        <Button onClick={handleCloseEscalateDialog} size={isMobile ? "small" : "medium"}>Cancel</Button>
                        <Button 
                            onClick={handleEscalate} 
                            variant="contained" 
                            color="error" 
                            disabled={actionLoading || !escalateForm.reason}
                            size={isMobile ? "small" : "medium"}
                        >
                            {actionLoading ? <CircularProgress size={20} /> : 'Escalate'}
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* Update Urgency Dialog */}
                <Dialog
                    open={openUrgencyDialog}
                    onClose={handleCloseUrgencyDialog}
                    maxWidth="sm"
                    fullWidth
                    PaperProps={{
                        sx: {
                            m: { xs: 1, sm: 2 },
                            width: { xs: 'calc(100% - 16px)', sm: 'auto' }
                        }
                    }}
                >
                    <DialogTitle sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                        Update Urgency Level
                    </DialogTitle>
                    <DialogContent>
                        <FormControl fullWidth sx={{ mt: 2 }} size={isMobile ? "small" : "medium"}>
                            <InputLabel>Urgency Level</InputLabel>
                            <Select 
                                value={urgencyForm.level} 
                                onChange={(e) => setUrgencyForm({ level: e.target.value })} 
                                label="Urgency Level"
                            >
                                <MenuItem value="normal">Normal</MenuItem>
                                <MenuItem value="urgent">Urgent</MenuItem>
                                <MenuItem value="critical">Critical</MenuItem>
                            </Select>
                        </FormControl>
                    </DialogContent>
                    <DialogActions sx={{ p: { xs: 2, sm: 3 } }}>
                        <Button onClick={handleCloseUrgencyDialog} size={isMobile ? "small" : "medium"}>Cancel</Button>
                        <Button onClick={handleUpdateUrgency} variant="contained" disabled={actionLoading} size={isMobile ? "small" : "medium"}>
                            {actionLoading ? <CircularProgress size={20} /> : 'Update'}
                        </Button>
                    </DialogActions>
                </Dialog>
            </FullWidthContainer>
        </>
    );
};

export default PendingReview;